import { Mol } from '@moleculeone/types';
import React, { useEffect, useState } from 'react';

import { styled } from '../../../stitches.config';
import { Icon } from '../../text/Icon/Icon';
import { CommonAtoms, CommonFragments, EditorSpacer, HotKeys, ModeToolbar, Toolbar } from '../BaseEditor/components';
import { useCompoundEditor } from '../BaseEditor/hooks/useCompoundEditor';
import { useKeyboard } from '../BaseEditor/hooks/useKeyboard';
import { molToSmiles } from '../BaseEditor/utils/conversion';
import { Button } from '../Button/Button';
import { ReactionEditorCompound } from './components/ReactionEditorCompound';

const EditorContainer = styled('div', {
  display: 'flex',
  paddingTop: '$5',
});

const DrawingSpacesContainer = styled('div', {
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  zIndex: 1,
  margin: '0 $4',
  variants: {
    minimal: {
      true: {
        height: '10rem',
      },
      false: {
        height: '30rem',
      },
    },
  },
});

const SubstratePlus = styled('div', {
  position: 'relative',
  fontSize: '$6',
  color: '$slate8',
  userSelect: 'none',
});

const ReactionArrow = styled('div', {
  'position': 'relative',
  'width': '6.5rem',
  '&::before': {
    content: '',
    zIndex: -1,
    top: '50%',
    display: 'block',
    position: 'absolute',
    left: 0,
    right: 0,
    height: '3px',
    marginTop: '-1.5px',
    backgroundColor: '$slate7',
  },
});
const ReactionArrowIcon = styled(Icon, {
  position: 'absolute',
  right: 5,
  top: '50%',
  fontSize: '$7',
  transform: 'translate(50%, -50%)',
  color: '$slate7',
  userSelect: 'none',
});

const TopContext = styled('div', {
  'position': 'relative',
  'zIndex': 2,
  'display': 'flex',
  'justifyContent': 'space-between',
  'alignItems': 'flex-end',
  'opacity': 0,
  'transform': 'translateY(-0.5rem)',
  'transition': 'opacity .2s, transform .2s',
  '&[data-visible=true]': {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

const LeftContainer = styled('div', {
  'position': 'relative',
  'zIndex': 2,
  'opacity': 0,
  'transform': 'translateX(-0.5rem)',
  'transition': 'opacity .2s, transform .2s',
  '&[data-visible=true]': {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

const RightContainer = styled('div', {
  'display': 'flex',
  'flexDirection': 'column',
  'justifyContent': 'space-between',
  'alignItems': 'flex-end',
  'opacity': 0,
  'transform': 'translateX(0.5rem)',
  'transition': 'opacity .2s, transform .2s',
  '&[data-visible=true]': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '&[hidden]': {
    opacity: 0,
    height: 0,
    pointerEvents: 'none',
  },
});
const RightToolbar = styled('div', {
  display: 'flex',
  gap: '$3',
  alignItems: 'flex-start',
});

const SwitchSubstrates = styled(Button, {
  position: 'absolute',
  top: '100%',
  left: 0,
});

type ReactionEditorProps = {
  loading: boolean;
  initialMols?: Mol[];
  smiles: {
    substrates: string[];
    product: string;
  };
  topContext?: React.ReactNode;
  arrowContext?: React.ReactNode;
  compoundChoices?: undefined | string[][];
  minimal: boolean;
  onChange: (data: { mols: Mol[]; smiles: string[] }) => void;
  compoundContext?: React.ReactNode[];
};
export const ReactionEditor = function ({
  loading,
  initialMols,
  smiles,
  arrowContext,
  topContext,
  compoundChoices,
  minimal,
  onChange,
  compoundContext,
}: ReactionEditorProps) {
  const { spaces, lastMolsUpdate, actions, mode, flipSubstrates } = useCompoundEditor();

  const [areMolsInitialized, setMolsInitialized] = useState(false);

  useEffect(() => {
    if (!loading && initialMols) {
      actions.setMols(initialMols, true);
    }
  }, [loading]);

  useEffect(() => {
    async function emitChange() {
      const mols = spaces.map(space => space.mol);

      const smiles = await Promise.all(mols.map(mol => molToSmiles(mol)));

      onChange({
        mols,
        smiles,
      });
    }

    if (areMolsInitialized) {
      emitChange();
    }

    setMolsInitialized(true);
  }, [lastMolsUpdate]);

  useKeyboard({
    onKeyDown: (event: KeyboardEvent) => {
      actions.onKeyDown(event);
    },
    onKeyUp: (event: KeyboardEvent) => {
      if (event.key !== '?') {
        actions.onKeyUp(event);
      }
    },
  });

  const substrates = spaces.slice(0, -1);
  const product = spaces.slice(-1)[0];

  const substratesCompoundChoices = compoundChoices?.slice(0, -1);
  const productCompoundChoices = compoundChoices?.slice(-1)[0];

  const substrateContexts = compoundContext?.slice(0, -1);
  const productContext = compoundContext?.slice(-1)[0];

  const isEmptyEditor = !smiles.product && smiles.substrates?.every(s => !s);
  const reactionSmiles = isEmptyEditor ? undefined : `${smiles.substrates.join('.')}>>${smiles.product}`;

  return (
    <>
      <EditorSpacer hidden={minimal}>
        <TopContext data-visible={!loading}>
          <Toolbar actions={actions} smiles={reactionSmiles} isReaction={true} />
          {topContext}
        </TopContext>
      </EditorSpacer>

      <EditorContainer>
        <LeftContainer data-visible={!loading}>
          <ModeToolbar hidden={minimal} />
        </LeftContainer>

        <DrawingSpacesContainer minimal={minimal}>
          {substrates.map((substrate, idx) => (
            <React.Fragment key={idx}>
              <ReactionEditorCompound
                loading={loading}
                spaceIdx={idx}
                compound={substrate}
                compoundChoices={substratesCompoundChoices?.[idx]}
                minimal={minimal}
                actions={actions}
                smiles={smiles.substrates[idx]}
                context={substrateContexts?.[idx]}
              />
              {idx < substrates.length - 1 && (
                <SubstratePlus>
                  +
                  <SwitchSubstrates onClick={flipSubstrates} weight="minimal" icon="swap_horiz" />
                </SubstratePlus>
              )}
            </React.Fragment>
          ))}

          <ReactionArrow>
            {arrowContext}
            <ReactionArrowIcon icon="arrow_right" />
          </ReactionArrow>

          <ReactionEditorCompound
            loading={loading}
            spaceIdx={2}
            compound={product}
            compoundChoices={productCompoundChoices}
            minimal={minimal}
            actions={actions}
            smiles={smiles.product}
            context={productContext}
          />
        </DrawingSpacesContainer>

        <RightContainer hidden={minimal} data-visible={!loading}>
          <RightToolbar>
            <CommonAtoms
              mode={mode}
              onPickAtom={mol => {
                actions.onFragmentAtomMode(mol, mol.atoms[0].idx);
              }}
            />

            <CommonFragments
              mode={mode}
              onPickAtom={(mol, idx) => {
                actions.onFragmentAtomMode(mol, idx);
              }}
              onPickBond={(mol, idx) => {
                actions.onFragmentBondMode(mol, idx);
              }}
            />
          </RightToolbar>

          <HotKeys />
        </RightContainer>
      </EditorContainer>
    </>
  );
};
